
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserInfo } from "@/types/strategy-types";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useModalStack } from "@/hooks/useModalStack";

interface UserProviderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (userId: string) => void;
  users: UserInfo[];
}

export const UserProviderDialog = ({
  isOpen,
  onClose,
  onAssign,
  users,
}: UserProviderDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { handleOpenChange } = useModalStack(isOpen, onClose);
  const dialogDescriptionId = "user-provider-dialog-description";
  
  // Filter to only show users with the 'strategy_provider' role
  const filteredUsers = users.filter(
    (user) => 
      user.role === 'strategy_provider' && 
      (user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
       `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby={dialogDescriptionId}>
        <DialogHeader>
          <DialogTitle>Assign User to Provider</DialogTitle>
          <DialogDescription id={dialogDescriptionId}>
            Select a strategy provider user to assign to this provider. Only users with the strategy provider role are shown.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="relative mb-4">
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          </div>

          <div className="max-h-[300px] overflow-auto border rounded-md">
            {filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No available strategy provider users found. Please ensure users have the strategy provider role assigned.
              </div>
            ) : (
              <div className="divide-y">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                    onClick={() => onAssign(user.id)}
                  >
                    <div>
                      <div className="font-medium">{user.email}</div>
                      <div className="text-sm text-gray-500">
                        {user.first_name || ''} {user.last_name || ''}
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Assign
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
